import { useState } from 'react'
import { getCountries, getCountryCallingCode, type CountryCode } from 'libphonenumber-js'
import { SearchableSelect } from '@/shared/ui/searchable-select'

const PREFERRED_COUNTRY_CODES: CountryCode[] = ['US', 'CA', 'GB', 'AU', 'NZ', 'IE']
const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })

export interface PhoneCodeOption {
  countryCode: CountryCode
  callingCode: string
  countryName: string
  label: string
}

// libphonenumber-js identifies countries by ISO code. It does not ship names or flag assets.
export const countryFlag = (countryCode: string): string => {
  if (!/^[A-Z]{2}$/.test(countryCode)) return ''

  return [...countryCode]
    .map((character) => String.fromCodePoint(127397 + character.charCodeAt(0)))
    .join('')
}

const phoneCodeOption = (countryCode: CountryCode): PhoneCodeOption => {
  const callingCode = `+${getCountryCallingCode(countryCode)}`
  const countryName = regionNames.of(countryCode) ?? countryCode
  const flag = countryFlag(countryCode)

  return {
    countryCode,
    callingCode,
    countryName,
    label: [flag, countryName, `(${callingCode})`].filter(Boolean).join(' '),
  }
}

const allPhoneCodeOptions = getCountries().map(phoneCodeOption)
const preferredCountryCodes = new Set<CountryCode>(PREFERRED_COUNTRY_CODES)

export const PHONE_CODE_OPTIONS: PhoneCodeOption[] = [
  ...PREFERRED_COUNTRY_CODES.flatMap((countryCode) => {
    const option = allPhoneCodeOptions.find((item) => item.countryCode === countryCode)
    return option ? [option] : []
  }),
  ...allPhoneCodeOptions
    .filter((option) => !preferredCountryCodes.has(option.countryCode))
    .sort((left, right) => left.countryName.localeCompare(right.countryName)),
]

const countryCodeForCallingCode = (callingCode: string): CountryCode | '' => {
  const normalizedCallingCode = callingCode.trim()
  const matches = PHONE_CODE_OPTIONS.filter(
    (option) => option.callingCode === normalizedCallingCode,
  )
  const preferred = PREFERRED_COUNTRY_CODES.find((countryCode) =>
    matches.some((option) => option.countryCode === countryCode),
  )

  return preferred ?? matches[0]?.countryCode ?? ''
}

interface PhoneCodeSelectProps {
  value: string
  onValueChange: (value: string) => void
  disabled?: boolean
  id?: string
  'aria-required'?: boolean
}

export const PhoneCodeSelect = ({
  value,
  onValueChange,
  disabled,
  id,
  'aria-required': ariaRequired,
}: PhoneCodeSelectProps) => {
  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode | ''>(() =>
    countryCodeForCallingCode(value),
  )
  const selectedOption = PHONE_CODE_OPTIONS.find(
    (option) => option.countryCode === selectedCountryCode,
  )
  const displayedCountryCode =
    selectedOption?.callingCode === value.trim()
      ? selectedCountryCode
      : countryCodeForCallingCode(value)

  return (
    <SearchableSelect
      id={id}
      value={displayedCountryCode}
      onValueChange={(countryCode) => {
        const option = PHONE_CODE_OPTIONS.find((item) => item.countryCode === countryCode)
        if (!option) return

        setSelectedCountryCode(option.countryCode)
        onValueChange(option.callingCode)
      }}
      options={PHONE_CODE_OPTIONS.map((option) => ({
        value: option.countryCode,
        label: option.label,
      }))}
      disabled={disabled}
      aria-required={ariaRequired}
    />
  )
}
